import { reportGlobalError, reportServerIncident } from '@/lib/observability';

/**
 * A served field read tolerantly (it degrades to a fallback rather than
 * failing the whole response) still tells the error tracker: a malformed
 * value is a contract regression, not an absence. Shared by every feature
 * that reads a field this way; `feature` names the one reporting.
 *
 * Once per contract per session in the browser, and per process on the
 * server, because these reads repeat (the vault record every minute, the
 * fee book after every claim) and the same slip would otherwise be
 * reported on every read. Only the contract's name travels, never the
 * served value, which can carry addresses.
 *
 * The vault record is also parsed on the server, by the per vault route's
 * prefetch, and then hydrated without a browser parse: there the browser
 * tracker does nothing, so the report goes to the server log instead.
 */

/** The contracts whose fallback has been reported. */
const reportedFallbacks = new Set<string>();

export function reportFallback<T>(
  feature: string,
  contract: string,
  fallback: T
) {
  return (ctx: { error: unknown }): T => {
    if (reportedFallbacks.has(contract)) return fallback;
    reportedFallbacks.add(contract);
    const context = { feature, contract };
    if (typeof window === 'undefined') {
      // "vaults.detail.blockNumber" reports as "vaults.detail fallback".
      const route = contract.split('.').slice(0, 2).join('.');
      reportServerIncident(`${route} fallback`, context);
    } else {
      void reportGlobalError(ctx.error, context);
    }
    return fallback;
  };
}

/** Test only: a fresh session, so no test depends on another's order. */
export function resetReportedFallbacks(): void {
  reportedFallbacks.clear();
}
